import { useState } from 'react';
import { Button, Flex, useTheme, spacing } from '@fti/design-system';

interface TagInputProps {
  tags: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
  label?: string;
}

export function TagInput({ tags, onChange, placeholder = 'Add a term...', label }: TagInputProps) {
  const [inputValue, setInputValue] = useState('');
  const { theme } = useTheme();

  const addTag = () => {
    const trimmed = inputValue.trim();
    if (trimmed && !tags.includes(trimmed)) {
      onChange([...tags, trimmed]);
      setInputValue('');
    }
  };

  const removeTag = (index: number) => {
    onChange(tags.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag();
    }
  };

  return (
    <div>
      {label && (
        <label
          style={{
            display: 'block',
            marginBottom: spacing.xs,
            fontSize: '0.85rem',
            fontWeight: 500,
            color: theme.colors.text.primary,
          }}
        >
          {label}
        </label>
      )}
      <Flex gap="xs" align="center" style={{ marginBottom: tags.length > 0 ? spacing.sm : 0 }}>
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          style={{
            flex: 1,
            padding: `${spacing.xs} ${spacing.sm}`,
            border: `1px solid ${theme.colors.border.default}`,
            borderRadius: '4px',
            backgroundColor: theme.colors.background.paper,
            color: theme.colors.text.primary,
            fontSize: '0.9rem',
          }}
        />
        <Button variant="outline" size="sm" onClick={addTag} disabled={!inputValue.trim()}>
          Add
        </Button>
      </Flex>
      {tags.length > 0 && (
        <Flex gap="xs" wrap="wrap">
          {tags.map((tag, index) => (
            <span
              key={index}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: spacing.xs,
                padding: `2px ${spacing.sm}`,
                backgroundColor: theme.colors.primary[100],
                color: theme.colors.text.primary,
                borderRadius: '12px',
                fontSize: '0.85rem',
              }}
            >
              {tag}
              <button
                onClick={() => removeTag(index)}
                style={{
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  fontSize: '1rem',
                  lineHeight: 1,
                  color: theme.colors.text.secondary,
                }}
                aria-label={`Remove ${tag}`}
              >
                &times;
              </button>
            </span>
          ))}
        </Flex>
      )}
    </div>
  );
}
